/**
 * Deals by Stage Handler
 * Query handler for getting deals grouped by pipeline stage
 */

import type { ToolExecutionContext } from '../../types/index.js';
import { QueryHandlerBase } from '../base/index.js';
import { createUserGraphQLClient } from '../../utils/graphql-client.js';

/**
 * Deals by stage handler
 * Groups deals by pipeline stage with counts and totals
 */
export class DealsByStageHandler extends QueryHandlerBase<any> {
  id = 'get_deals_by_stage';

  schema = {
    type: 'object',
    properties: {
      company_id: {
        type: 'string',
        description: 'Optional: Filter to a specific company',
      },
    },
    required: [],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<any> {
    if (!context?.userToken) {
      throw new Error('User token is required for deal retrieval');
    }

    try {
      const { GetDealssDocument } = await import('@/src/gql/graphql');
      const client = createUserGraphQLClient(context.userToken);

      // Build filter
      const filter = params.company_id ? { company_id: { eq: params.company_id } } : undefined;

      const result = await client.query({
        query: GetDealssDocument,
        variables: {
          first: 1000, // Get all deals for grouping
          filter,
          orderBy: [{ stage: 'AscNullsLast' }, { revenue: 'DescNullsLast' }],
        },
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        throw new Error(`GraphQL error: ${result.error.message}`);
      }

      const data = result.data as any;
      const deals = data?.dealsCollection?.edges?.map((edge: any) => edge?.node).filter((node: any) => node != null) || [];

      // Group deals by stage
      const stages = ['lead', 'qualified', 'discovery', 'proposal', 'negotiation', 'contract', 'won', 'lost'];
      const groupedByStage: Record<string, any> = {};

      stages.forEach((stage) => {
        const stageDeals = deals.filter((d: any) => d.stage === stage);
        const totalValue = stageDeals.reduce((sum: number, d: any) => sum + (d.amount || 0), 0);
        const avgProbability = stageDeals.length > 0 
          ? stageDeals.reduce((sum: number, d: any) => sum + (d.close_probability || 50), 0) / stageDeals.length 
          : 0;

        groupedByStage[stage] = {
          count: stageDeals.length,
          totalValue: Math.round(totalValue * 100) / 100,
          averageProbability: Math.round(avgProbability * 100) / 100,
          deals: stageDeals.map((d: any) => ({
            id: d.id,
            title: d.title,
            company_name: d.company_name,
            amount: d.amount,
            close_probability: d.close_probability,
            close_date: d.close_date,
            viewInApp: `[${d.title}](/deals/${d.id})`,
          })),
        };
      });

      return {
        byStage: groupedByStage,
        summary: {
          totalDeals: deals.length,
          totalPipelineValue: Math.round(
            deals
              .filter((d: any) => d.stage !== 'won' && d.stage !== 'lost')
              .reduce((sum: number, d: any) => sum + (d.amount || 0), 0) * 100
          ) / 100,
          wonDeals: groupedByStage.won.count,
          lostDeals: groupedByStage.lost.count,
        },
      };
    } catch (error) {
      throw new Error(`Deal grouping failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
